import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { MediaViewerService } from '@shared/services/media-viewer.service';
import { fallbackToDefaultThumbnail } from '@shared/utils/exercise-display.utils';

/**
 * The app's one media lightbox, mounted once by the layout. It renders whatever
 * {@link MediaViewerService} currently holds — nothing hands it a URL directly,
 * so the service's guards are the only door in.
 *
 * One dialog for images and videos alike: they are the same gesture on the same
 * kind of asset, and splitting them across PrimeNG's `p-image` preview and a
 * bespoke video dialog is what made two cards in one section behave differently.
 */
@Component({
  selector: 'app-media-viewer-dialog',
  standalone: true,
  imports: [DialogModule, Button],
  template: `
    <!-- Closing always goes through the service: the dialog holds no state of
         its own, so letting PrimeNG hide it while the service still read
         "visible" would leave a video mounted and playing. -->
    <p-dialog
      [header]="title()"
      [modal]="true"
      [visible]="visible()"
      (visibleChange)="onVisibleChange($event)"
      [draggable]="false"
      [resizable]="false"
      [dismissableMask]="true"
      [style]="{ width: '50rem' }"
      [breakpoints]="{ '1199px': '75vw', '575px': '90vw' }"
    >
      <div class="flex items-center justify-center bg-surface-100 dark:bg-surface-900 rounded overflow-hidden">
        @switch (kind()) {
          @case ('image') {
            <img [src]="src()" [alt]="title()" (error)="onImageError($event)"
                 class="max-w-full object-contain" style="max-height: 70vh"/>
          }
          @case ('video') {
            @if (videoFailed()) {
              <!-- Videos created before they became uploaded files hold a Vimeo id
                   and no file at all, so their URL answers 404. Say that, instead
                   of leaving a black rectangle that never starts. -->
              <div class="flex flex-col items-center gap-2 py-10 px-4 text-center">
                <i class="pi pi-exclamation-triangle text-muted-color" style="font-size: 2rem"></i>
                <span class="text-muted-color" i18n>Il video non è disponibile. Ricaricalo dagli asset dell'esercizio.</span>
              </div>
            } @else {
              <!-- The overflow menu is trimmed to picture-in-picture alone:
                   downloading an asset is not something this viewer offers, and
                   playback rate and casting have no meaning for a therapy clip.
                   controlsList is honoured by Chromium only — Firefox and
                   Safari render their own control set and ignore it, so this
                   hides the entries rather than guaranteeing they are gone. -->
              <video [src]="src()" controls preload="metadata" [title]="title()"
                     controlsList="nodownload noplaybackrate noremoteplayback"
                     (error)="onVideoError()"
                     class="w-full bg-black" style="max-height: 70vh"></video>
            }
          }
        }
      </div>

      <!-- No download action, by design. It could not be made honest anyway: the
           download hint on an anchor is ignored cross-origin (the API is its own
           host) and nothing sets Content-Disposition, so what a click did was
           decided by the object's Content-Type — an extensionless catalog asset
           downloaded, an uploaded .mp4 opened in a new tab instead. -->
      <div class="flex justify-end gap-2 mt-5">
        <p-button i18n-label label="Chiudi" severity="secondary" (onClick)="close()"/>
      </div>
    </p-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaViewerDialogComponent {
  private readonly viewer = inject(MediaViewerService);

  readonly visible = computed(() => this.viewer.state().visible);
  readonly kind = computed(() => this.viewer.state().kind);
  readonly src = computed(() => this.viewer.state().src);

  /** Reset on every open, so a working video after a broken one still plays. */
  private readonly failedSrc = signal<string | null>(null);
  readonly videoFailed = computed(() => !!this.src() && this.failedSrc() === this.src());
  readonly title = computed(() => this.viewer.state().title ?? $localize`Anteprima`);

  /** ESC, the header's X and the mask all land here. */
  onVisibleChange(visible: boolean): void {
    if (!visible) this.close();
  }

  close(): void {
    this.viewer.close();
  }

  protected onImageError(event: Event): void {
    fallbackToDefaultThumbnail(event);
  }

  /** Keyed on the URL, not a bare boolean, so it clears itself on the next open. */
  protected onVideoError(): void {
    this.failedSrc.set(this.src());
  }
}
